import { MEDIA } from "./media";

// Content for the config-driven service / product landing pages.
export const SERVICE_PAGES = {
  "printing-branding": {
    path: "/printing-branding",
    breadcrumb: "Printing & Branding",
    seoTitle: "Printing and Branding Cape Town | DTF, Vinyl & Embroidery | KhozaLand",
    seoDescription: "Printing and branding in Cape Town by KhozaLand, Mitchells Plain. DTF and heat-transfer printing, vinyl, embroidery, promotional printing and custom branding. Request a quote.",
    eyebrow: "Printing & Branding Services",
    h1: "Printing & Branding Services in Cape Town",
    intro: "From a single personalised shirt to branded clothing for your whole team, KhozaLand helps individuals, businesses and organisations put their names, logos and messages onto products that people actually use.",
    category: "printing-branding",
    whatsapp: "general",
    primaryCta: "Request a Printing Quote",
    hero: [MEDIA.golfShirtClan, MEDIA.mugsEvent],
    serviceType: "Printing and branding",
    sections: [
      {
        h2: "Branding methods, explained simply",
        lead: "You don't need to know printing terms to order. Tell us what you want to achieve — we'll recommend the right finish for your product, design and quantity.",
        items: [
          { title: "DTF Printing", text: "Direct-to-film printing transfers full-colour designs, photos and detailed logos onto fabric. Ideal when your artwork has many colours or fine detail." },
          { title: "Heat-Transfer Printing", text: "Designs are pressed onto garments and items using heat. A versatile option for names, numbers, logos and personalised gifts." },
          { title: "Vinyl Printing & Cutting", text: "Bold, clean single-colour lettering and shapes — like clan names, slogans and team names — cut from vinyl and applied with heat." },
          { title: "Embroidery", text: "Stitched logos and names for a durable, professional finish on uniforms and corporate clothing." },
          { title: "Promotional Printing", text: "Banners, stickers and promotional items to help your business, church or event get noticed." },
          { title: "Custom Branding & Selected Logo Design", text: "Need help with a layout or a simple logo? Selected design services are available on request — ask us in your enquiry." },
        ],
      },
      {
        h2: "What you can brand",
        lead: "Choose the product first — then we'll match the printing method.",
        links: [
          { label: "Custom T-shirts & branded clothing", to: "/custom-tshirt-printing" },
          { label: "Uniforms & embroidery", to: "/uniforms-embroidery" },
          { label: "Corporate gifts & promotional products", to: "/corporate-gifts" },
          { label: "Personalised gifts", to: "/personalised-gifts" },
          { label: "Banners, stickers & promotional printing", to: "/banners-stickers" },
          { label: "Xhosa cultural products", to: "/xhosa-cultural-products" },
        ],
      },
    ],
    note: "Depending on the product and finish, some production work may be completed with trusted partners. Your quotation confirms the method, timeline and price.",
    productSet: null,
    faqKeys: ["ownLogo", "ownDesign", "prices", "production", "urgent"],
    related: ["/custom-tshirt-printing", "/uniforms-embroidery", "/bulk-orders"],
  },

  "custom-tshirt-printing": {
    path: "/custom-tshirt-printing",
    breadcrumb: "Custom T-Shirts & Clothing",
    seoTitle: "Custom T-Shirt Printing Cape Town | Branded T-Shirts & Golf Shirts | KhozaLand",
    seoDescription: "Custom T-shirt printing in Cape Town and Mitchells Plain. Branded T-shirts, golf shirts, jackets, family and church clothing from R250. Request a custom clothing quote.",
    eyebrow: "Custom Apparel",
    h1: "Custom T-Shirts & Branded Clothing",
    intro: "Branded T-shirts, golf shirts and jackets for businesses, families, churches, teams and events — printed with your logo, names or design in your choice of style.",
    category: "custom-clothing",
    whatsapp: "tshirts",
    primaryCta: "Request a Custom Clothing Quote",
    hero: [MEDIA.golfShirtClan],
    serviceType: "Custom T-shirt printing",
    sections: [
      {
        h2: "Clothing for every occasion",
        items: [
          { title: "Custom T-shirts", text: "Short- and long-sleeve crew necks with your logo, slogan, names or photos." },
          { title: "Golf shirts", text: "Collared golfers for a smart branded look at work and functions." },
          { title: "Jackets", text: "Branded jackets for staff, teams and organisations." },
          { title: "Corporate clothing", text: "Consistent branded clothing that represents your business professionally." },
          { title: "Family shirts", text: "Matching shirts for reunions, birthdays, memorials and celebrations." },
          { title: "Church & group clothing", text: "Matching clothing for churches, burial societies, choirs and community groups." },
        ],
      },
      {
        h2: "What to include in your quote request",
        lead: "The more detail you share, the faster we can prepare an accurate quotation.",
        checklist: ["Product type (T-shirt, golf shirt, jacket…)", "Quantity", "Sizes needed", "Garment colours", "Your logo or design", "Preferred delivery date", "Delivery area", "Budget, if known"],
      },
    ],
    productSet: "clothing",
    productsTitle: "Clothing you can customise",
    faqKeys: ["tshirtsCapeTown", "ownLogo", "churchSociety", "bulk", "production"],
    related: ["/uniforms-embroidery", "/bulk-orders", "/printing-branding"],
  },

  "uniforms-embroidery": {
    path: "/uniforms-embroidery",
    breadcrumb: "Uniforms & Embroidery",
    seoTitle: "Branded Uniforms Cape Town | Workwear Branding & Embroidery | KhozaLand",
    seoDescription: "Branded uniforms in Cape Town: business uniforms, workwear branding, school and group clothing and custom embroidery from KhozaLand in Mitchells Plain. Request a uniform quote.",
    eyebrow: "Uniforms & Embroidery",
    h1: "Branded Uniforms & Embroidery",
    intro: "Give your team a consistent, professional look. KhozaLand brands uniforms and clothing for businesses, schools, churches and community organisations — printed or embroidered with your logo.",
    category: "uniforms-embroidery",
    whatsapp: "uniforms",
    primaryCta: "Request a Uniform Quote",
    hero: [MEDIA.golfShirtClan],
    serviceType: "Branded uniforms and embroidery",
    sections: [
      {
        h2: "Uniform solutions",
        items: [
          { title: "Business uniforms", text: "Branded golf shirts, T-shirts and jackets that help customers recognise your staff." },
          { title: "Workwear branding", text: "Add your logo to everyday workwear garments. Tell us the garment and we'll advise on the best finish." },
          { title: "School & group clothing", text: "Branded clothing for schools, preschools, sports teams, choirs and community groups." },
          { title: "Embroidered apparel", text: "Stitched logos and names for a durable, premium finish." },
          { title: "Corporate clothing", text: "Smart branded clothing for offices, events and representatives." },
        ],
      },
    ],
    note: "We brand everyday uniforms and clothing. Specialist industrial protective clothing is not currently advertised — please ask if you have a specific requirement.",
    productSet: "uniforms",
    productsTitle: "Popular uniform items",
    faqKeys: ["uniformsMitchellsPlain", "ownLogo", "bulk", "minimum", "deposit"],
    related: ["/custom-tshirt-printing", "/bulk-orders", "/corporate-gifts"],
  },

  "xhosa-cultural-products": {
    path: "/xhosa-cultural-products",
    breadcrumb: "Xhosa Cultural Products",
    seoTitle: "Xhosa Cultural Blankets South Africa | Ingcawe & Ibhayi lombhaco | KhozaLand",
    seoDescription: "Personalised Xhosa cultural products: Ingcawe/Ngcawe blankets with clan names and iziduko, Ibhayi lombhaco and clan-name clothing. Made in Cape Town, enquiries from across South Africa.",
    eyebrow: "Heritage · Xhosa cultural products",
    h1: "Xhosa Cultural Products & Personalised Blankets",
    intro: "Celebrate who you are and where you come from. KhozaLand creates personalised Xhosa cultural products — Ingcawe/Ngcawe blankets printed with your clan name and iziduko, Ibhayi lombhaco and clan-name clothing — made with care and respect for tradition.",
    category: "xhosa-cultural",
    whatsapp: "cultural",
    primaryCta: "Enquire About Cultural Products",
    secondaryCta: { label: "Request a Custom Design", product: "Custom cultural design" },
    hero: [MEDIA.blanketRhudulu, MEDIA.blanketMngwevu],
    serviceType: "Personalised Xhosa cultural products",
    cultural: true,
    sections: [
      {
        h2: "Our cultural products",
        items: [
          { title: "Ingcawe / Ngcawe Blankets", text: "Traditional white blankets with black stripes, personalised with clan names, clan praises (iziduko), family names and chosen illustrations." },
          { title: "Ibhayi lombhaco", text: "Traditional Xhosa garment made from umbhaco cloth. Enquire about design, colours and personalisation." },
          { title: "Ibhayi lesintu", text: "Available on enquiry — contact us to confirm current availability." },
          { title: "Clan-name clothing", text: "Golf shirts and T-shirts printed with clan names and heritage-inspired artwork for families and gatherings." },
        ],
      },
      {
        h2: "How personalisation works",
        lead: "Every family's identity is unique. Share the details that matter to you and we'll confirm the layout with you before production.",
        checklist: ["Product (Ingcawe, Ibhayi lombhaco, clothing)", "Clan name and iziduko wording", "Preferred motifs or illustrations", "Colours", "Quantity", "Delivery area and required date"],
      },
    ],
    note: "Please check the spelling of clan names and iziduko carefully when you send your wording — we print exactly what you approve.",
    productSet: "cultural",
    productsTitle: "Cultural products",
    faqKeys: ["ingcawe", "lombhaco", "culturalProducts", "delivery", "deposit"],
    related: ["/gallery", "/custom-tshirt-printing", "/personalised-gifts"],
  },

  "corporate-gifts": {
    path: "/corporate-gifts",
    breadcrumb: "Corporate Gifts",
    seoTitle: "Corporate Gifts Cape Town | Branded Promotional Products | KhozaLand",
    seoDescription: "Corporate gifts and promotional products in Cape Town: branded mugs, tumblers, candles, water bottles and corporate apparel personalised with your logo. Request a corporate gift quote.",
    eyebrow: "For Businesses & Organisations",
    h1: "Corporate Gifts & Promotional Products",
    intro: "Thank clients, reward staff and promote your brand with useful, personalised products. KhozaLand brands corporate gifts with your company name, logo or message.",
    category: "corporate-gifts",
    whatsapp: "corporate",
    primaryCta: "Request a Corporate Gift Quote",
    hero: [MEDIA.mugsEvent, MEDIA.candlesChurch],
    serviceType: "Corporate gifts and promotional products",
    sections: [
      {
        h2: "Gifts that carry your brand",
        items: [
          { title: "Staff & client gifts", text: "Branded mugs, tumblers and candles for year-end, milestones and appreciation." },
          { title: "Events & launches", text: "Personalised items for conferences, community events, launches and commemorations." },
          { title: "Corporate apparel", text: "Branded golf shirts and clothing for staff and promotions." },
          { title: "Individually named items", text: "Each gift can carry a different name — perfect for recognition events." },
        ],
      },
    ],
    productSet: "corporate",
    productsTitle: "Promotional products",
    faqKeys: ["corporateGifts", "bulk", "ownLogo", "minimum", "production"],
    related: ["/personalised-gifts", "/bulk-orders", "/uniforms-embroidery"],
  },

  "personalised-gifts": {
    path: "/personalised-gifts",
    breadcrumb: "Personalised Gifts",
    seoTitle: "Personalised Gifts Cape Town | Mugs, Tumblers, Cushions & Candles | KhozaLand",
    seoDescription: "Personalised gifts in Cape Town for every occasion: personalised mugs from R120, custom tumblers, photo cushions, candles and water bottles. Enquire on WhatsApp or request a quote.",
    eyebrow: "Gifts with meaning",
    h1: "Personalised Gifts for Every Occasion",
    intro: "Birthdays, weddings, memorials, church events and “just because” — turn names, photos and messages into gifts people keep.",
    category: "personalised-gifts",
    whatsapp: "gifts",
    primaryCta: "Request a Gift Quote",
    hero: [MEDIA.cushionsTote, MEDIA.candlesChurch],
    serviceType: "Personalised gifts",
    sections: [
      {
        h2: "Make every gift personal",
        items: [
          { title: "Birthdays & celebrations", text: "Photo cushions, mugs and tumblers with names and messages." },
          { title: "Church & community events", text: "Personalised candles and mugs with names, dates and verses." },
          { title: "Memorials", text: "Gentle keepsakes featuring photos and words that honour loved ones." },
          { title: "Families", text: "Matching personalised items for reunions and gatherings." },
        ],
      },
    ],
    productSet: "gifts",
    productsTitle: "Personalised products",
    faqKeys: ["ownLogo", "prices", "production", "urgent", "delivery"],
    related: ["/corporate-gifts", "/xhosa-cultural-products", "/gallery"],
  },

  "banners-stickers": {
    path: "/banners-stickers",
    breadcrumb: "Banners & Stickers",
    seoTitle: "Banners and Stickers Cape Town | Vinyl & Promotional Printing | KhozaLand",
    seoDescription: "Banners, custom stickers, vinyl graphics and promotional printing for Cape Town businesses, churches and events. Share your design and request a printing quote from KhozaLand.",
    eyebrow: "Promotional Printing",
    h1: "Banners, Stickers & Promotional Printing",
    intro: "Help people notice your business, church or event. KhozaLand takes enquiries for banners, stickers, vinyl graphics and promotional materials.",
    category: "banners-stickers",
    whatsapp: "printing",
    primaryCta: "Request a Printing Quote",
    hero: [],
    serviceType: "Banners, stickers and promotional printing",
    sections: [
      {
        h2: "Promotional printing options",
        items: [
          { title: "Banners", text: "For shopfronts, events, services and stalls. Share your preferred size and we'll confirm options." },
          { title: "Stickers", text: "Logo stickers, labels and event stickers for packaging and promotion." },
          { title: "Vinyl graphics", text: "Cut vinyl lettering and logos for branding applications." },
          { title: "Promotional materials", text: "Branded items to support launches, campaigns and community events." },
        ],
      },
    ],
    note: "Available sizes, materials and finishes are confirmed in your quotation.",
    productSet: "printing",
    productsTitle: "Printing enquiries",
    faqKeys: ["quote", "ownLogo", "ownDesign", "production"],
    related: ["/printing-branding", "/corporate-gifts", "/bulk-orders"],
  },
};

export const PAGE_TITLES = {
  "/printing-branding": "Printing & Branding",
  "/custom-tshirt-printing": "Custom T-Shirts & Clothing",
  "/uniforms-embroidery": "Uniforms & Embroidery",
  "/xhosa-cultural-products": "Xhosa Cultural Products",
  "/corporate-gifts": "Corporate Gifts",
  "/personalised-gifts": "Personalised Gifts",
  "/banners-stickers": "Banners & Stickers",
  "/bulk-orders": "Bulk & Group Orders",
  "/gallery": "Our Work",
  "/about": "About KhozaLand",
  "/faqs": "FAQs",
  "/contact": "Request a Quote",
};
